import { Button as VuiButton, Checkbox as VuiCheckbox, Link as VuiLink, Text } from '@village/ui';
import { device } from 'citymd/constants/device';
import { Banner } from 'components/banner';
import { rem } from 'polished';
import styled, { css } from 'styled-components';

// The app follows the SCMD design, the website keeps the CityMD web look
const APP_WARNING_SURFACE = '#FCF3D4';
const APP_WARNING_ICON = '#5E4E1F';
const WEBSITE_WARNING_SURFACE = '#f1f2f4';
const WEBSITE_WARNING_ICON = '#cc9800';
// The website design's condition headings are smaller than the theme's h4, which the step title uses
const WEBSITE_CONDITION_TITLE_FONT_SIZE = rem(20);
// The app's webview runs under the iPhone home indicator without reporting the safe-area inset
const HOME_INDICATOR_HEIGHT = rem(34);

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${rem(40)};
    scroll-margin-top: ${rem(78)};

    @media ${device.maxMobile} {
        height: 100%;
    }
`;

const DetailsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${rem(24)};
`;

const Condition = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${rem(8)};
`;

const ConditionTitle = styled(Text)<{ $isWebsite: boolean }>`
    color: ${({ theme }) => theme.vui.colors.text.default};
    font-family: CeraProBold, arial, sans-serif;

    ${({ $isWebsite }) =>
        $isWebsite &&
        css`
            font-size: ${WEBSITE_CONDITION_TITLE_FONT_SIZE};
        `}
`;

const RequirementList = styled.ul`
    margin: 0;
    padding-left: ${rem(28)};
`;

const WarningBanner = styled(Banner)<{ $isWebsite: boolean }>`
    align-items: flex-start;
    gap: ${({ $isWebsite }) => ($isWebsite ? rem(8) : rem(12))};
    border-radius: ${({ $isWebsite }) => ($isWebsite ? 0 : rem(12))};
    background: ${({ $isWebsite }) => ($isWebsite ? WEBSITE_WARNING_SURFACE : APP_WARNING_SURFACE)};

    & svg {
        color: ${({ $isWebsite }) => ($isWebsite ? WEBSITE_WARNING_ICON : APP_WARNING_ICON)};
    }
`;

const Link = styled(VuiLink)<{ $isWebsite: boolean }>`
    text-decoration: underline;

    &,
    &:link,
    &:visited {
        color: ${({ theme, $isWebsite }) => ($isWebsite ? 'inherit' : theme.vui.colors.text.link.default)};
    }
`;

const Checkbox = styled(VuiCheckbox)<{ $isChecked: boolean; $isWebsite: boolean }>`
    &.MuiFormControlLabel-root {
        align-items: flex-start;
        gap: ${rem(12)};
        margin: 0;
    }

    .MuiCheckbox-root {
        flex-shrink: 0;
        padding: ${rem(2)} 0 0;
    }

    .MuiFormControlLabel-label {
        font-family: CeraProRegular, arial, sans-serif;
    }

    ${({ theme, $isChecked, $isWebsite }) =>
        $isWebsite
            ? css`
                  svg {
                      width: ${rem(20)};
                      height: ${rem(20)};
                      color: ${$isChecked ? theme.vui.colors.icon.default : theme.vui.colors.border.default.default};
                  }
              `
            : css`
                  .MuiCheckbox-root {
                      width: ${rem(20)};
                      height: ${rem(20)};
                      margin-top: ${rem(2)};
                      padding: 0;
                      border: ${rem(2)} solid ${theme.vui.colors.border.tertiary.active};
                      border-radius: ${rem(4)};

                      &,
                      &:hover {
                          background: ${$isChecked ? theme.vui.colors.border.tertiary.active : 'transparent'};
                      }

                      &.Mui-focusVisible {
                          outline: ${rem(2)} solid ${theme.vui.colors.border.tertiary.active};
                          outline-offset: ${rem(2)};
                      }

                      /* white check mark drawn in place of the MUI icon */
                      ${$isChecked &&
                      css`
                          &::after {
                              content: '';
                              width: ${rem(5)};
                              height: ${rem(10)};
                              margin-top: -${rem(2)};
                              border: solid ${theme.vui.colors.background.white};
                              border-width: 0 ${rem(2)} ${rem(2)} 0;
                              transform: rotate(45deg);
                          }
                      `}

                      svg,
                      .MuiTouchRipple-root {
                          display: none;
                      }
                  }
              `}
`;

const ButtonWrapper = styled.div<{ $isWebsite: boolean }>`
    display: flex;
    flex: 1;
    justify-content: center;
    align-items: flex-end;

    ${({ $isWebsite }) =>
        !$isWebsite &&
        css`
            /* keeps Continue clear of the iPhone home indicator once the page is scrolled to the bottom */
            @media ${device.maxMobile} {
                padding-bottom: calc(${rem(24)} + max(env(safe-area-inset-bottom), ${HOME_INDICATOR_HEIGHT}));
            }
        `}
`;

const Button = styled(VuiButton)`
    margin: 0 auto;
    width: ${rem(217)};
    height: ${rem(56)};

    @media ${device.maxMobile} {
        width: 100%;
        max-width: ${rem(341)};
    }
`;

export {
    Button,
    ButtonWrapper,
    Checkbox,
    Condition,
    ConditionTitle,
    Container,
    DetailsContainer,
    Link,
    RequirementList,
    WarningBanner,
};
